import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import Button from '../../../../incase-domain/components/button/Button';
import Modal from '../../../../incase-domain/components/modals/directory/Modal';
import FileUpload from '../../FileUpload';
import Label from '../../../../incase-domain/components/label/Label';
import {loggedInUserSelector} from '../../../../store/selectors/auth.selectors';
import {Role} from '../../../../incase-domain/user';
import {AIPresentationMetaData} from '../../../../incase-domain/AIPresentationMetaData';
import {useGenerateAIPresentation} from './queries';
import AIGenerationCard from './AIGenerationCard';

interface AiGeneratePresentationProps {
    buttonLabel: string;
}

enum WizardStep {
    UPLOAD = 'UPLOAD',
    PREVIEW = 'PREVIEW',
    RESULT = 'RESULT',
}

function AiGeneratePresentation({buttonLabel}: AiGeneratePresentationProps): JSX.Element | null {
    const {t} = useTranslation();

    const loggedUser = useSelector(loggedInUserSelector);
    const navigate = useNavigate();

    const allowedUsersForAiGenerate = [Role.SUPERUSER];
    const [isWizardOpen, setIsWizardOpen] = useState(false);
    const [wizardStep, setWizardStep] = useState<WizardStep>(WizardStep.UPLOAD);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);

    const {
        mutate: generatePresentation,
        isLoading,
        data: generatedPresentation,
    } = useGenerateAIPresentation({
        onSuccess: () => {
            setWizardStep(WizardStep.RESULT);
        },
        onError: () => {
            setWizardStep(WizardStep.UPLOAD);
        },
    });

    const handleCancel = (): void => {
        setIsWizardOpen(false);
        setSelectedFile(null);
        setWizardStep(WizardStep.UPLOAD);
    };

    const handleFileChange = (files: FileList): void => {
        const acceptedExtensions = ['.html', '.txt', '.md'];
        const acceptedMimeTypes = ['text/html', 'text/plain', 'text/markdown', 'text/x-markdown'];

        const textFiles = Array.from(files).filter((file) => {
            const fileName = file.name.toLowerCase();
            const hasValidExtension = acceptedExtensions.some((ext) => fileName.endsWith(ext));
            const hasValidMimeType = acceptedMimeTypes.includes(file.type);
            return hasValidExtension || hasValidMimeType;
        });

        if (textFiles.length > 0) {
            setSelectedFile(textFiles[0]);
            setWizardStep(WizardStep.PREVIEW);
        } else {
            toast.error(t('aiGeneratePresentation.invalidFileType'));
        }
    };

    const handleGenerate = (): void => {
        if (selectedFile) {
            setIsGenerating(true);
            // setWizardStep(WizardStep.GENERATING);

            const reader = new FileReader();
            reader.readAsText(selectedFile);
            reader.onload = () => {
                if (reader.result) {
                    const aiMetadata: AIPresentationMetaData = {
                        content: [reader.result as string],
                        images: [],
                    };

                    generatePresentation(aiMetadata);
                }
            };

            reader.onerror = () => {
                setWizardStep(WizardStep.UPLOAD);
                toast.error(t('aiGeneratePresentation.fileReadError'));
            };
        }
    };

    const handleStart = (): void => {
        if (generatedPresentation) {
            navigate(`/presentations/${generatedPresentation._id}`);
            handleCancel();
        }
    };

    const renderStep = (): JSX.Element => {
        switch (wizardStep) {
            case WizardStep.PREVIEW:
                return (
                    <div className="c-aiGenerateModal__preview">
                        <AIGenerationCard>
                            <div className="c-aiGenerateModal__preview__file">
                                <Button padded={false} variant="ghost" leading="solar:file-linear" size="2xl" />
                                <div className="c-aiGenerateModal__preview__file__name">
                                    <Label size="md">{selectedFile?.name}</Label>
                                    <Label size="xs">{((selectedFile?.size || 0) / 1024).toFixed(1)} kB</Label>
                                </div>
                            </div>
                            <Button
                                variant="solid"
                                size="xs"
                                color="primary"
                                onClick={handleGenerate}
                                disabled={isLoading || !selectedFile}
                                loading={isLoading || isGenerating}
                            >
                                {t('aiGeneratePresentation.generateCase')}
                            </Button>
                        </AIGenerationCard>
                    </div>
                );

            case WizardStep.RESULT:
                return (
                    <div className="c-aiGenerateModal__preview">
                        <AIGenerationCard>
                            <div className="c-aiGenerateModal__preview__file">
                                <Button padded={false} variant="ghost" leading="solar:check-circle-linear" size="2xl" />
                                <Label size="md">{t('aiGeneratePresentation.resultReady')}</Label>
                            </div>
                            <Button variant="solid" color="primary" onClick={handleStart}>
                                {t('aiGeneratePresentation.open')}
                            </Button>
                        </AIGenerationCard>
                    </div>
                );

            case WizardStep.UPLOAD:
            default:
                return <FileUpload onChange={handleFileChange} />;
        }
    };

    if (!loggedUser?.roles?.some((role) => allowedUsersForAiGenerate.includes(role))) return null;

    return (
        <>
            <Button
                variant="solid"
                color="frost"
                size="md"
                leading="healthicons:artificial-intelligence"
                onClick={() => setIsWizardOpen(true)}
            >
                {buttonLabel || t('aiGeneratePresentation.generatePresentation')}
            </Button>

            <Modal
                isOpen={isWizardOpen}
                onCancel={handleCancel}
                size="md"
                showCancel
                icon="healthicons:artificial-intelligence"
                header={t('aiGeneratePresentation.generatePresentation')}
                description={t('aiGeneratePresentation.generatePresentationDescription')}
            >
                <div className="c-aiGenerateModal">{renderStep()}</div>
            </Modal>
        </>
    );
}

export default AiGeneratePresentation;
